import { useBookAppointmentContext } from '@/components/modules/modals/BookAppointment/BookAppointment.hooks';
import { SCREEN_NAME } from '@/constants/analytics';
import { exportIdSlug, getBookingStartingPoint, promiseWrapper, setBookingStartingPoint } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { useAppDispatch, useAppSelector } from '@/hooks';
import useInitialLoading from '@/hooks/useInitialLoading';
import { DEAL_DETAIL_ACTION } from '@/reducers/dealDetailReducer';
import { DEAL_IMPRESSION, impressionService } from '@/services/impressionService';
import { AppDispatch } from '@/store';
import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { dealsService } from '../../services';
import { dealDetailsErrorResponseSchema, dealDetailsResponseSchema } from '../../types/dealDetails';
import { handleBackClick, handleBookClick, handlePageLeave } from './DealDetails.handlers';

/**
 * Expected location state for the deal details page
 * when coming from the deals serp
 */
type DealDetailsLocationState = {
  searchedKeyword?: string;
  searchedLocation?: string;
};

async function fetchAndSetDeal(id: string, signal: AbortSignal, dispatch: AppDispatch) {
  dispatch({ type: DEAL_DETAIL_ACTION.SET_FETCHING });

  const response = await promiseWrapper(dealsService.details({ id, signal }));

  if (response.error?.name === 'AbortError') {
    return;
  }

  const status = response.error?.response?.status;
  const isErrorStatus = status === 404 || status === 410;
  const dataToParse = isErrorStatus ? response.error.response.data : response.data;

  const parsed = dealDetailsResponseSchema.safeParse(dataToParse);
  if (parsed.success) {
    dispatch({
      type: DEAL_DETAIL_ACTION.SET_DATA,
      payload: { deal: parsed.data.deal, searchPreview: parsed.data.sections },
    });
    return;
  }

  const errorParsed = dealDetailsErrorResponseSchema.safeParse(dataToParse);
  if (errorParsed.success) {
    dispatch({
      type: DEAL_DETAIL_ACTION.SET_ERROR,
      payload: {
        errorType: errorParsed.data.error.type,
        deal: errorParsed.data.error.deal,
        searchPreview: errorParsed.data.sections,
      },
    });
    return;
  }

  dispatch({
    type: DEAL_DETAIL_ACTION.SET_ERROR,
    payload: { errorType: 'error', deal: null, searchPreview: [] },
  });
}

/**
 * Custom hook for deal details page
 * Handles data fetching, navigation, and state management
 */
export const useDealDetails = () => {
  const { slugId } = useParams<{ slugId: string }>();
  const history = useHistory();
  const location = useLocation<DealDetailsLocationState>();
  const { searchedKeyword, searchedLocation } = location.state || {};
  const state = useAppSelector((state) => state.dealDetail);
  const { is404 } = useAppSelector((state) => state.loading);
  const user = useAppSelector((state) => state.users)?.user;
  const appDispatch = useAppDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);
  const context = useBookAppointmentContext();
  const { id } = exportIdSlug(slugId);
  const { deal = null, searchPreview = [] } = state?.data || {};
  const isCurrentDealSSRFetched = state.isSSRFetched && deal?.id && deal.id.toString() === id;
  const initialLoading = useInitialLoading(state.fetching, isCurrentDealSSRFetched);

  useEffect(() => {
    if (isCurrentDealSSRFetched || state.errorType) return;

    if (is404) {
      appDispatch({
        type: DEAL_DETAIL_ACTION.SET_ERROR,
        payload: { errorType: 'not_found', deal: null, searchPreview: [] },
      });
      return;
    }

    if (!slugId) {
      appDispatch({
        type: DEAL_DETAIL_ACTION.SET_ERROR,
        payload: { errorType: 'not_found', deal: null, searchPreview: [] },
      });
      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    fetchAndSetDeal(id, abortControllerRef.current.signal, appDispatch);
    window.scrollTo(0, 0);

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [id]);

  useEffect(() => {
    trackMpEvent({ name: 'screen_shown', properties: { screen_name: SCREEN_NAME.DEAL_DETAILS } });

    if (!getBookingStartingPoint()) {
      setBookingStartingPoint(SCREEN_NAME.DEAL_DETAILS);
    }
  }, []);

  const impressionSentForDealRef = useRef<number | null>(null);
  useEffect(() => {
    if (!deal?.id || !deal.campaignId) return;
    if (impressionSentForDealRef.current === deal.id) return;
    impressionSentForDealRef.current = deal.id;
    impressionService.storeDealImpressions({
      items: [{ dealId: deal.id, placeId: deal.place.id }],
      type: DEAL_IMPRESSION.DEAL_IMPRESSION,
    });
  }, [deal?.id, deal?.campaignId]);

  useEffect(() => {
    return () => {
      appDispatch({ type: DEAL_DETAIL_ACTION.SET_RESET });
    };
  }, [appDispatch]);

  const onBackClick = useCallback(
    () => handleBackClick(history, searchedKeyword, searchedLocation),
    [history, searchedKeyword, searchedLocation],
  );

  const onBookClick = useCallback(
    () => handleBookClick(state.data.deal, user, appDispatch, abortControllerRef, context, history),
    [state.data, user, appDispatch, context],
  );

  useEffect(() => {
    const unlisten = history.listen((_location, action) => {
      if (action === 'POP') {
        handlePageLeave();
      }
    });
    return unlisten;
  }, [history]);

  return {
    deal,
    searchPreview,
    loading: initialLoading || state.fetching,
    errorType: state.errorType,
    slugId,
    searchedKeyword,
    searchedLocation,
    onBackClick,
    onBookClick,
  };
};
